import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import { FC, useMemo } from 'react';

import { filters as tabFilters, FiltersType, ViewType, viewsFilters } from '../services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makeStyles((theme: any) => ({
  tabs: {
    flex: 1,
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.button.default,
    height: theme.spacing(2),
  },
  tabsIndicator: { backgroundColor: theme.accent.main },
  tabRoot: {
    padding: 0,
    textTransform: 'none',
    minWidth: theme.spacing(12),
    maxWidth: theme.spacing(22),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    '&.Mui-selected': { color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.primary.main },
  },
  tabWrapper: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  viewLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
  },
  viewLabelActive: { color: theme.palette.secondary.main },
  viewLabelInactive: {
    color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.grey[600],
  },
  desktopTabs: {
    display: 'flex',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  mobileSelectors: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
      width: '100%',
    },
  },
  mobileSelector: {
    flex: 1,
    minWidth: 0,
  },
}));

type OrderTabsProps = {
  t: TFunction;
  filter: FiltersType;
  handleChangeFilter: (value: FiltersType) => void;
  viewTabValue: ViewType;
  onViewTabValueChange: (value: ViewType) => void
};

const OrderTabs: FC<OrderTabsProps> = ({ t, filter, handleChangeFilter, viewTabValue, onViewTabValueChange }) => {
  const classes = useStyles();

  const memorizedFilters = useMemo(() => tabFilters(t), [t]);
  const memoizedViewFilters = useMemo(() => viewsFilters(t), [t]);

  return (
    <Grid container direction="column" wrap="nowrap">
      <Grid item className={classes.desktopTabs}>
        <Grid container alignItems="center">
          <Grid item xs={10}>
            <Tabs
              value={filter}
              textColor="primary"
              className={classes.tabs}
              onChange={(_, value) => handleChangeFilter(value)}
            >
              {memorizedFilters.map((memorizedFilter) => (
                <Tab
                  key={memorizedFilter.id}
                  value={memorizedFilter.value}
                  label={memorizedFilter.label}
                  classes={{ root: classes.tabRoot, wrapped: classes.tabWrapper }}
                />
              ))}
            </Tabs>
          </Grid>

          <Grid item xs={2}>
            <Tabs
              value={viewTabValue}
              textColor="primary"
              className={classes.tabs}
              onChange={(_, value) => onViewTabValueChange(value)}
            >
              {memoizedViewFilters.map(({ id, value, label, icon }) => (
                <Tab
                  key={id}
                  value={value}
                  label={(
                    <Box className={`${classes.viewLabel} ${value === viewTabValue ? classes.viewLabelActive : classes.viewLabelInactive}`}>
                      <FontAwesomeIcon icon={icon} />
                      {label}
                    </Box>
                  )}
                  classes={{ root: classes.tabRoot, wrapped: classes.tabWrapper }}
                />
              ))}
            </Tabs>
          </Grid>
        </Grid>
      </Grid>

      <Grid item className={classes.mobileSelectors}>
        <FormControl variant="outlined" size="small" className={classes.mobileSelector}>
          <InputLabel id="orders-status-select-label">{t('orders:status')}</InputLabel>
          <Select
            labelId="orders-status-select-label"
            value={filter}
            label={t('orders:status')}
            onChange={(event) => handleChangeFilter(event.target.value as FiltersType)}
          >
            {memorizedFilters.map(({ id, value, label }) => (
              <MenuItem key={id} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.mobileSelector}>
          <InputLabel id="orders-view-select-label">{t('orders:view')}</InputLabel>
          <Select
            labelId="orders-view-select-label"
            value={viewTabValue}
            label={t('orders:view')}
            onChange={(event) => onViewTabValueChange(event.target.value as ViewType)}
          >
            {memoizedViewFilters.map(({ id, value, label }) => (
              <MenuItem key={id} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default OrderTabs;
